/**
 * Sign in to PrivacyLens — supports both:
 *   1. Google (Emergent OAuth)  — calls startLogin()
 *   2. Email + password         — calls POST /api/auth/login, then refresh()
 *
 * The page also surfaces a "Verify your email" hint after a fresh signup
 * (state passed via location.state.verifyPending) and a recovery banner
 * after a password reset.
 */
import { useState, useEffect } from "react";
import { useNavigate, useLocation, Link } from "react-router-dom";
import { Mail, Lock, Loader2, LogIn, AlertCircle } from "lucide-react";
import { toast } from "sonner";

import { apiClient } from "../lib/api";
import { useAuth, startLogin } from "../lib/auth";

function formatApiErrorDetail(detail) {
  if (detail == null) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail
      .map((e) => (e && typeof e.msg === "string" ? e.msg : null))
      .filter(Boolean)
      .join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return "Login failed. Please try again.";
}

export default function Login() {
  const navigate = useNavigate();
  const location = useLocation();
  const { refresh, user, loading: authLoading } = useAuth();

  const [email, setEmail] = useState(location.state?.email || "");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);
  const [resending, setResending] = useState(false);
  const [unverified, setUnverified] = useState(
    Boolean(location.state?.verifyPending),
  );

  // If already signed in, bounce to dashboard
  useEffect(() => {
    if (!authLoading && user) {
      navigate(location.state?.returnTo || "/dashboard", { replace: true });
    }
  }, [user, authLoading, navigate, location.state]);

  const submit = async (e) => {
    e.preventDefault();
    setErr(null);
    setBusy(true);
    try {
      await apiClient.post(
        "/auth/login",
        { email, password },
        { withCredentials: true },
      );
      await refresh();
      navigate(location.state?.returnTo || "/dashboard", { replace: true });
    } catch (ex) {
      const detail = ex?.response?.data?.detail;
      if (ex?.response?.status === 403) {
        setUnverified(true);
        setErr(formatApiErrorDetail(detail));
      } else {
        setErr(formatApiErrorDetail(detail));
      }
    } finally {
      setBusy(false);
    }
  };

  const resend = async () => {
    if (!email) {
      toast.error("Enter your email above first.");
      return;
    }
    setResending(true);
    try {
      await apiClient.post("/auth/resend-verification", { email });
      toast.success("Verification email resent. Check your inbox.");
    } catch {
      toast.error("Could not resend right now. Try again in a minute.");
    } finally {
      setResending(false);
    }
  };

  return (
    <main className="border-b border-black min-h-[80vh]" data-testid="login-page">
      <section className="max-w-md mx-auto px-6 lg:px-8 py-16">
        <div className="font-mono text-[11px] uppercase tracking-widest text-[#0047FF] mb-3">
          / Sign in
        </div>
        <h1 className="font-display text-4xl md:text-5xl tracking-tight leading-[1.05] mb-2">
          Welcome back.
        </h1>
        <p className="text-[#475569] text-sm mb-8">
          Access your dashboard, monitor scans, and download your purchased Pro
          documents.
        </p>

        {unverified && (
          <div
            className="border border-[#F59E0B] bg-[#FFF7ED] p-3 mb-4 flex items-start gap-2"
            data-testid="login-unverified-banner"
          >
            <AlertCircle className="w-4 h-4 text-[#F59E0B] flex-shrink-0 mt-0.5" />
            <div className="text-xs text-[#0A0A0A] leading-relaxed">
              Your email isn&apos;t verified yet. Check your inbox or{" "}
              <button
                type="button"
                onClick={resend}
                disabled={resending}
                className="underline font-medium hover:text-[#0047FF] disabled:opacity-60"
                data-testid="resend-verification-btn"
              >
                {resending ? "sending…" : "resend the link"}
              </button>
              .
            </div>
          </div>
        )}

        <form onSubmit={submit} className="space-y-3" data-testid="login-form">
          <label className="block">
            <span className="font-mono text-[10px] uppercase tracking-widest text-[#475569]">
              Email
            </span>
            <div className="mt-1 flex items-center border border-black bg-white">
              <Mail className="w-4 h-4 mx-3 text-[#94A3B8]" />
              <input
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="w-full px-1 py-2.5 text-sm outline-none"
                placeholder="you@company.com"
                data-testid="login-email-input"
                autoComplete="email"
              />
            </div>
          </label>

          <label className="block">
            <span className="font-mono text-[10px] uppercase tracking-widest text-[#475569]">
              Password
            </span>
            <div className="mt-1 flex items-center border border-black bg-white">
              <Lock className="w-4 h-4 mx-3 text-[#94A3B8]" />
              <input
                type="password"
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="w-full px-1 py-2.5 text-sm outline-none"
                placeholder="••••••••"
                data-testid="login-password-input"
                autoComplete="current-password"
              />
            </div>
          </label>

          {err && (
            <p
              className="text-xs text-[#B91C1C] bg-[#FEE2E2] border border-[#FCA5A5] px-3 py-2"
              data-testid="login-error"
            >
              {err}
            </p>
          )}

          <button
            type="submit"
            disabled={busy}
            className="w-full bg-[#0A0A0A] text-white py-3 inline-flex items-center justify-center gap-2 disabled:opacity-60 hover:bg-[#0047FF] transition-colors"
            data-testid="login-submit-button"
          >
            {busy ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              <LogIn className="w-4 h-4" />
            )}
            {busy ? "Signing in…" : "Sign in"}
          </button>

          <div className="flex justify-between items-center text-xs pt-1">
            <Link
              to="/forgot-password"
              className="text-[#475569] hover:text-[#0047FF] underline"
              data-testid="forgot-password-link"
            >
              Forgot password?
            </Link>
            <Link
              to="/signup"
              className="text-[#475569] hover:text-[#0047FF] underline"
              data-testid="signup-link"
            >
              Create an account
            </Link>
          </div>
        </form>

        <div className="my-6 flex items-center gap-3">
          <div className="flex-1 h-px bg-[#E2E8F0]" />
          <span className="font-mono text-[10px] uppercase tracking-widest text-[#94A3B8]">
            Or
          </span>
          <div className="flex-1 h-px bg-[#E2E8F0]" />
        </div>

        <button
          type="button"
          onClick={() => startLogin(location.state?.returnTo || "/dashboard")}
          className="w-full border border-black bg-white py-3 inline-flex items-center justify-center gap-2 hover:bg-[#F1F5F9]"
          data-testid="login-google-button"
        >
          <svg width="16" height="16" viewBox="0 0 24 24" aria-hidden="true">
            <path
              fill="#4285F4"
              d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09Z"
            />
            <path
              fill="#34A853"
              d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.99.66-2.25 1.06-3.71 1.06-2.86 0-5.29-1.93-6.15-4.53H2.18v2.84A11 11 0 0 0 12 23Z"
            />
            <path
              fill="#FBBC05"
              d="M5.85 14.1A6.6 6.6 0 0 1 5.5 12c0-.73.13-1.43.35-2.1V7.07H2.18A11 11 0 0 0 1 12c0 1.78.43 3.46 1.18 4.93l3.67-2.83Z"
            />
            <path
              fill="#EA4335"
              d="M12 5.38c1.62 0 3.06.56 4.2 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1A11 11 0 0 0 2.18 7.07l3.67 2.83C6.71 7.31 9.14 5.38 12 5.38Z"
            />
          </svg>
          Continue with Google
        </button>

        <p className="text-[10px] text-center text-[#94A3B8] mt-6">
          By signing in you accept our{" "}
          <a
            href="/legal/terms.html"
            target="_blank"
            rel="noopener noreferrer"
            className="underline"
          >
            Terms
          </a>{" "}
          &amp;{" "}
          <a
            href="/legal/privacy.html"
            target="_blank"
            rel="noopener noreferrer"
            className="underline"
          >
            Privacy Policy
          </a>
          .
        </p>
      </section>
    </main>
  );
}
